'use client';

import { useEffect, useState } from 'react';

import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Icon,
    IconName,
    Price,
    PriceLevel,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TrilogyColor,
    TypographyBold,
} from '@bytel/trilogy-react';

import { OptionType, PlanType } from '@app-types/product';

import { OFFER_BLOCKED } from '@constants/app';

import { useHome } from '@landing/hooks';

import { BlockedProductOption } from './blocked-product-option';

interface ProductCatalogItemProps {
    product: PlanType;
    isHalfWidth: boolean;
    blockedOption?: OptionType;
}

export function ProductCatalogItem({
    product,
    isHalfWidth,
    blockedOption: _blockedOption,
}: Readonly<ProductCatalogItemProps>) {
    const { handleAddCatalogProductToCart, byouCart } = useHome();
    const [isOfferBlocked, setIsOfferBlocked] = useState(false);
    const blockedOption = _blockedOption ? [_blockedOption] : [];

    useEffect(() => {
        if (product.locked !== '1') return;
        const t = byouCart?.options?.find((o) => o.gencode === String(OFFER_BLOCKED));
        if (t) {
            setIsOfferBlocked(true);
        }
    }, [byouCart]);

    const handleCheck = () => {
        setIsOfferBlocked(!isOfferBlocked);
    };

    const onAddToCart = () => {
        let options: OptionType[] = [];
        if (product.locked === '1' && isOfferBlocked) {
            options = blockedOption;
        }

        handleAddCatalogProductToCart({ product, options });
    };
    return (
        <Column size={isHalfWidth ? 6 : 12}>
            <Box fullheight flat shadowless>
                <BoxContent backgroundColor={TrilogyColor.INFO_FADE}>
                    <Columns verticalAlign={Align.CENTER} marginless mobile>
                        <Column>
                            <Text level={TextLevels.TWO} marginless>
                                Forfait B&YOU
                            </Text>
                            <Text typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD]} marginless>
                                {product.dataEnvelope}
                            </Text>
                            <Text marginless level={TextLevels.FOUR}>
                                sans engagement
                            </Text>
                        </Column>
                        <Column narrow>
                            <Price amount={product.price} level={PriceLevel.FIVE} period='mois' />
                        </Column>
                    </Columns>
                </BoxContent>
                <BoxContent>
                    <Rows gap={4}>
                        <Row>
                            <Rows gap={4}>
                                <Row>
                                    {product.locked === '1' && (
                                        <BlockedProductOption
                                            product={product}
                                            isOfferBlocked={isOfferBlocked}
                                            handleCheck={handleCheck}
                                        />
                                    )}
                                </Row>

                                <Row narrow>
                                    <Columns mobile gap={2} verticalAlign={Align.CENTER}>
                                        <Column narrow>
                                            <Icon name={IconName.AT} />
                                        </Column>
                                        <Column>
                                            <Text level={TextLevels.THREE}>
                                                <strong>&nbsp;{product.dataEnvelope} d&apos;internet</strong> dont{' '}
                                                {product.roamingEnvelope ?? ' 0Go'} utilisables en Europe
                                            </Text>
                                        </Column>
                                    </Columns>
                                </Row>
                                <Row narrow>
                                    <Columns mobile gap={2} verticalAlign={Align.CENTER}>
                                        <Column narrow>
                                            <Icon name={IconName.PHONE_INFINITY} />
                                        </Column>
                                        <Column>
                                            <Text level={TextLevels.THREE}>
                                                &nbsp;Appels &amp; SMS illimités, MMS <strong>illimités</strong> en
                                                France métropolitaine
                                            </Text>
                                        </Column>
                                    </Columns>
                                </Row>
                                <Row narrow>
                                    <Columns mobile gap={2} verticalAlign={Align.CENTER}>
                                        <Column narrow>
                                            <Icon name={IconName.SHOOTING_STAR} />
                                        </Column>
                                        <Column>
                                            <Text level={TextLevels.THREE}>
                                                <strong>
                                                    &nbsp;La qualité du réseau{' '}
                                                    {product.maxConnections === '5g' ? '5G' : '4G'}
                                                </strong>{' '}
                                                de Bouygues Telecom
                                            </Text>
                                        </Column>
                                    </Columns>
                                </Row>
                            </Rows>
                        </Row>
                        <Row narrow>
                            <Spacer size={SpacerSize.FIVE} />
                            <Button variant={ButtonVariant.PRIMARY} fullwidth onClick={onAddToCart}>
                                Ajouter au panier
                            </Button>
                        </Row>
                    </Rows>
                </BoxContent>
            </Box>
        </Column>
    );
}
